/**
 * Notification Settings Tab
 *
 * Manages notification preferences:
 * - Notification preference (all/mentions/none)
 * - Desktop notifications
 * - Sound notifications with per-type customization
 * - Custom sound upload and management
 * - Email notifications
 */

import { useState, useEffect, useRef } from 'react';
import { useUserSettings, useUpdateSettings } from '@/hooks/useUserSettings';
import { useCustomSounds, useUploadCustomSound, useDeleteCustomSound } from '@/hooks/useCustomSounds';
import { soundService, type SoundType } from '@/services/sound.service';
import { DNDToggle } from './DNDToggle';
import { isPushSupported, isPushSubscribed, subscribeToPush, unsubscribeFromPush } from '@/services/push.service';
import { asApiError } from '@/utils/error.util';

const MAX_SOUND_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm'];
const MAX_CUSTOM_SOUNDS = 10;

export const NotificationSettings = () => {
  const { data: settings } = useUserSettings();
  const updateSettings = useUpdateSettings();
  const { data: customSounds } = useCustomSounds();
  const uploadCustomSound = useUploadCustomSound();
  const deleteCustomSound = useDeleteCustomSound();

  const [notificationPreference, setNotificationPreference] = useState('all');
  const [desktopEnabled, setDesktopEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported] = useState(isPushSupported());
  const [pushLoading, setPushLoading] = useState(false);

  // Custom sound upload state
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check push subscription state on mount
  useEffect(() => {
    if (pushSupported) {
      isPushSubscribed().then(setPushEnabled);
    }
  }, [pushSupported]);

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
      } else {
        const success = await subscribeToPush();
        setPushEnabled(success);
      }
    } finally {
      setPushLoading(false);
    }
  };

  // Per-notification-type sound settings
  const [messageSound, setMessageSound] = useState<SoundType>('default');
  const [mentionSound, setMentionSound] = useState<SoundType>('bell');
  const [dmSound, setDmSound] = useState<SoundType>('chime');
  const [systemSound, setSystemSound] = useState<SoundType>('pop');
  const [voiceJoinSound, setVoiceJoinSound] = useState<SoundType>('voiceJoin');
  const [voiceLeaveSound, setVoiceLeaveSound] = useState<SoundType>('voiceLeave');
  const [callRingtone, setCallRingtone] = useState<SoundType>('callRingtone');

  const [messageVolume, setMessageVolume] = useState(50);
  const [mentionVolume, setMentionVolume] = useState(70);
  const [dmVolume, setDmVolume] = useState(60);
  const [systemVolume, setSystemVolume] = useState(50);
  const [voiceJoinVolume, setVoiceJoinVolume] = useState(50);
  const [voiceLeaveVolume, setVoiceLeaveVolume] = useState(50);
  const [callRingtoneVolume, setCallRingtoneVolume] = useState(70);

  useEffect(() => {
    if (settings) {
      setNotificationPreference(settings.notification_preference);
      setDesktopEnabled(settings.desktop_notifications_enabled);
      setSoundEnabled(settings.sound_notifications_enabled);
      setEmailEnabled(settings.email_notifications_enabled);

      // Load per-type sound settings
      setMessageSound(settings.message_sound as SoundType);
      setMentionSound(settings.mention_sound as SoundType);
      setDmSound(settings.dm_sound as SoundType);
      setSystemSound(settings.system_sound as SoundType);
      setVoiceJoinSound((settings.voice_join_sound || 'voiceJoin') as SoundType);
      setVoiceLeaveSound((settings.voice_leave_sound || 'voiceLeave') as SoundType);
      setMessageVolume(settings.message_volume);
      setMentionVolume(settings.mention_volume);
      setDmVolume(settings.dm_volume);
      setSystemVolume(settings.system_volume);
      setVoiceJoinVolume(settings.voice_join_volume ?? 50);
      setVoiceLeaveVolume(settings.voice_leave_volume ?? 50);
      setCallRingtone((settings.call_ringtone || 'callRingtone') as SoundType);
      setCallRingtoneVolume(settings.call_ringtone_volume ?? 70);
    }
  }, [settings]);

  const handleUpdate = async (field: string, value: string | number | boolean) => {
    try {
      await updateSettings.mutateAsync({ [field]: value });
    } catch (error) {
      console.error('Failed to update notification settings:', error);
    }
  };

  const handleTestSound = async (soundType: SoundType, volume: number) => {
    try {
      await soundService.testSound(soundType, volume);
    } catch (error) {
      console.error('Failed to play test sound:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError('');
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      setUploadError('Невалиден формат. Позволени: MP3, WAV, OGG, WebM');
      return;
    }

    if (file.size > MAX_SOUND_SIZE) {
      setUploadError('Файлът е твърде голям. Максимум: 2MB');
      return;
    }

    setUploadFile(file);
    // Auto-fill name from filename if empty
    if (!uploadName) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setUploadName(nameWithoutExt);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;

    setUploadError('');
    try {
      await uploadCustomSound.mutateAsync({ file: uploadFile, name: uploadName.trim() });
      setUploadFile(null);
      setUploadName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setUploadError(asApiError(error).response?.data?.message || 'Грешка при качване');
    }
  };

  const handleDeleteSound = async (soundId: string) => {
    try {
      soundService.clearCustomSoundCache(soundId);
      await deleteCustomSound.mutateAsync(soundId);
    } catch (error) {
      console.error('Failed to delete custom sound:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderSoundControl = (
    label: string,
    description: string,
    soundType: SoundType,
    volume: number,
    onSoundChange: (value: SoundType) => void,
    onVolumeChange: (value: number) => void,
    soundField: string,
    volumeField: string
  ) => (
    <div className="space-y-3 p-4 border border-gray-200 dark:border-dark-border-primary rounded-lg bg-gray-50 dark:bg-dark-bg-secondary">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{label}</h4>
        <p className="text-xs text-gray-500 dark:text-dark-text-secondary">{description}</p>
      </div>

      {/* Sound Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          Звук
        </label>
        <select
          value={soundType}
          onChange={(e) => {
            const newSound = e.target.value as SoundType;
            onSoundChange(newSound);
            handleUpdate(soundField, newSound);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-md text-sm bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary"
        >
          <optgroup label="Синтезирани звуци">
            <option value="default">По подразбиране</option>
            <option value="bell">Камбана</option>
            <option value="chime">Звънец</option>
            <option value="pop">Поп</option>
            <option value="mention">Споменаване</option>
            <option value="voiceJoin">Присъединяване</option>
            <option value="voiceLeave">Напускане</option>
          </optgroup>
          <optgroup label="Аудио файлове">
            <option value="pleasant">Приятен</option>
            <option value="message">Съобщение</option>
            <option value="received">Получено</option>
            <option value="received2">Получено 2</option>
            <option value="alert">Сигнал</option>
            <option value="pop2">Поп 2</option>
          </optgroup>
          {customSounds && customSounds.length > 0 && (
            <optgroup label="Потребителски звуци">
              {customSounds.map((sound) => (
                <option key={sound.id} value={`custom:${sound.id}`}>
                  {sound.name}
                </option>
              ))}
            </optgroup>
          )}
          <option value="none">Без звук</option>
        </select>
      </div>

      {/* Volume Control */}
      {soundType !== 'none' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            Сила на звука: {volume}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => onVolumeChange(parseInt(e.target.value))}
            onMouseUp={() => handleUpdate(volumeField, volume)}
            onTouchEnd={() => handleUpdate(volumeField, volume)}
            className="w-full"
          />
        </div>
      )}

      {/* Test Button */}
      {soundType !== 'none' && (
        <button
          onClick={() => handleTestSound(soundType, volume)}
          className="w-full px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
          Тествай звука
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Do Not Disturb Toggle */}
      <div className="p-4 border border-gray-200 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-secondary">
        <DNDToggle />
      </div>

      {/* Notification Preference */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Известявай ме за
        </h3>
        <div className="space-y-2">
          {[
            { value: 'all', label: 'Всички съобщения', desc: 'Получавай известие за всяко съобщение' },
            { value: 'mentions', label: 'Само споменавания', desc: 'Само когато някой ви спомене' },
            { value: 'none', label: 'Нищо', desc: 'Заглуши всички известия' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
            >
              <input
                type="radio"
                name="notification-preference"
                value={option.value}
                checked={notificationPreference === option.value}
                onChange={(e) => {
                  setNotificationPreference(e.target.value);
                  handleUpdate('notification_preference', e.target.value);
                }}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{option.label}</div>
                <div className="text-xs text-gray-500 dark:text-dark-text-secondary">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Desktop Notifications */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Известия на работния плот</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Показвай системни известия</div>
        </div>
        <button
          onClick={() => {
            const newValue = !desktopEnabled;
            setDesktopEnabled(newValue);
            handleUpdate('desktop_notifications_enabled', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            desktopEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              desktopEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Background Push Notifications */}
      {pushSupported && (
        <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Известия във фонов режим</div>
            <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Получавай известия дори когато HighFive е затворен</div>
          </div>
          <button
            onClick={handlePushToggle}
            disabled={pushLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              pushEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
            } ${pushLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                pushEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      {/* Sound Notifications */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Звукови известия</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Възпроизвеждай звук при нови съобщения</div>
        </div>
        <button
          onClick={() => {
            const newValue = !soundEnabled;
            setSoundEnabled(newValue);
            handleUpdate('sound_notifications_enabled', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            soundEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              soundEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Per-Type Sound Settings */}
      {soundEnabled && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-200 dark:border-dark-border-primary">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
            Персонализиране на звуци
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Message Sounds */}
            {renderSoundControl(
              'Звуци за съобщения',
              'Обикновени съобщения в канали',
              messageSound,
              messageVolume,
              setMessageSound,
              setMessageVolume,
              'message_sound',
              'message_volume'
            )}

            {/* Mention Sounds */}
            {renderSoundControl(
              'Звуци за споменавания',
              '@споменавания и отговори',
              mentionSound,
              mentionVolume,
              setMentionSound,
              setMentionVolume,
              'mention_sound',
              'mention_volume'
            )}

            {/* DM Sounds */}
            {renderSoundControl(
              'Лични съобщения',
              'Известия за лични съобщения',
              dmSound,
              dmVolume,
              setDmSound,
              setDmVolume,
              'dm_sound',
              'dm_volume'
            )}

            {/* System Sounds */}
            {renderSoundControl(
              'Системни известия',
              'Системни съобщения и обновления',
              systemSound,
              systemVolume,
              setSystemSound,
              setSystemVolume,
              'system_sound',
              'system_volume'
            )}

            {/* Voice Join Sounds */}
            {renderSoundControl(
              'Присъединяване към глас',
              'Когато някой се присъедини към гласов канал',
              voiceJoinSound,
              voiceJoinVolume,
              setVoiceJoinSound,
              setVoiceJoinVolume,
              'voice_join_sound',
              'voice_join_volume'
            )}

            {/* Voice Leave Sounds */}
            {renderSoundControl(
              'Напускане на глас',
              'Когато някой напусне гласов канал',
              voiceLeaveSound,
              voiceLeaveVolume,
              setVoiceLeaveSound,
              setVoiceLeaveVolume,
              'voice_leave_sound',
              'voice_leave_volume'
            )}

            {/* Call Ringtone */}
            {renderSoundControl(
              'Рингтон за обаждане',
              'Когато някой ви се обажда',
              callRingtone,
              callRingtoneVolume,
              setCallRingtone,
              setCallRingtoneVolume,
              'call_ringtone',
              'call_ringtone_volume'
            )}
          </div>

          {/* Custom Sound Management */}
          <div className="mt-6 p-4 border border-gray-200 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-secondary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
                Потребителски звуци
              </h3>
              <span className="text-xs text-gray-500 dark:text-dark-text-secondary">
                {customSounds?.length || 0} / {MAX_CUSTOM_SOUNDS}
              </span>
            </div>

            {/* Existing Custom Sounds List */}
            {customSounds && customSounds.length > 0 && (
              <div className="space-y-2 mb-4">
                {customSounds.map((sound) => (
                  <div
                    key={sound.id}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-bg-tertiary rounded-md"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
                        {sound.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-secondary">
                        {sound.original_filename} &middot; {formatFileSize(sound.file_size)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={() => handleTestSound(`custom:${sound.id}` as SoundType, 50)}
                        className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Тест"
                      >
                        &#9654;
                      </button>
                      <button
                        onClick={() => handleDeleteSound(sound.id)}
                        disabled={deleteCustomSound.isPending}
                        className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                        title="Изтрий"
                      >
                        &#10005;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Form */}
            {(!customSounds || customSounds.length < MAX_CUSTOM_SOUNDS) && (
              <div className="space-y-3 border-t border-gray-200 dark:border-dark-border-primary pt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    Име на звука
                  </label>
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="Напр. Моят звук"
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-md text-sm bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    Аудио файл (MP3, WAV, OGG, WebM &middot; макс. 2MB)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.wav,.ogg,.webm,audio/mpeg,audio/wav,audio/ogg,audio/webm"
                    onChange={handleFileSelect}
                    className="w-full text-sm text-gray-500 dark:text-dark-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 dark:file:bg-blue-900/20 file:text-blue-600 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/30"
                  />
                </div>

                {uploadError && (
                  <p className="text-xs text-red-600 dark:text-red-400">{uploadError}</p>
                )}

                <button
                  onClick={handleUpload}
                  disabled={!uploadFile || !uploadName.trim() || uploadCustomSound.isPending}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadCustomSound.isPending ? 'Качване...' : 'Качи звук'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Notifications */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Известия по имейл</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Получавай обобщения по имейл</div>
        </div>
        <button
          onClick={() => {
            const newValue = !emailEnabled;
            setEmailEnabled(newValue);
            handleUpdate('email_notifications_enabled', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            emailEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              emailEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
